/**
 * Componente Badge
 *
 * Etiqueta/badge para status e categorização.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { HTMLAttributes } from 'react';

/**
 * Variantes do badge
 */
type BadgeVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'gray';

/**
 * Tamanhos do badge
 */
type BadgeSize = 'sm' | 'md' | 'lg';

/**
 * Props do componente Badge
 */
interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  dot?: boolean;
}

/**
 * Classes CSS por variante
 */
const variantClasses: Record<BadgeVariant, string> = {
  primary:
    'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400',
  secondary:
    'bg-secondary-100 text-secondary-800 dark:bg-secondary-900/30 dark:text-secondary-400',
  success:
    'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-400',
  warning:
    'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-400',
  error:
    'bg-error-100 text-error-800 dark:bg-error-900/30 dark:text-error-400',
  info: 'bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-400',
  gray: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
};

/**
 * Classes CSS para o dot por variante
 */
const dotClasses: Record<BadgeVariant, string> = {
  primary: 'bg-primary-500',
  secondary: 'bg-secondary-500',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  error: 'bg-error-500',
  info: 'bg-info-500',
  gray: 'bg-gray-500',
};

/**
 * Classes CSS por tamanho
 */
const sizeClasses: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-base',
};

/**
 * Componente Badge
 */
export function Badge({
  variant = 'gray',
  size = 'md',
  dot = false,
  className = '',
  children,
  ...props
}: BadgeProps) {
  const baseClasses = 'inline-flex items-center gap-1.5 font-medium rounded-full';

  const classes = [
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes} {...props}>
      {dot && (
        <span
          className={`h-1.5 w-1.5 rounded-full ${dotClasses[variant]}`}
        />
      )}
      {children}
    </span>
  );
}
